<template>
  <div class="channel-selector">
    <!-- Dropdown menu (appears above the chip when open) -->
    <div v-if="isDropdownOpen" class="channel-dropdown">
      <button
        v-for="channel in availableChannels"
        :key="channel.value"
        class="channel-option"
        @click="selectChannel(channel.value)"
      >
        <span class="option-label">{{ channel.label }}</span>
      </button>
    </div>

    <!-- Chip -->
    <div
      class="channel-chip"
      :class="channelClass"
      @click="toggleDropdown"
    >
      <span class="channel-label">{{ currentChannelLabel }}</span>
      <span class="dropdown-icon" :class="{ 'is-open': isDropdownOpen }"></span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MessageChannelSelector',
  props: {
    modelValue: {
      type: String,
      default: 'in_app'
    }
  },
  emits: ['change', 'update:modelValue'],
  data() {
    return {
      currentChannel: 'in_app', // 'in_app', 'sms', 'email'
      isDropdownOpen: false,
      channels: [
        { value: 'in_app', label: 'In-App' },
        { value: 'sms', label: 'SMS' },
        { value: 'email', label: 'Email' }
      ]
    };
  },
  computed: {
    currentChannelLabel() {
      return this.channels.find(c => c.value === this.currentChannel)?.label || 'In-App';
    },
    channelClass() {
      return `channel-${this.currentChannel}`;
    },
    availableChannels() {
      // Return all channels except the currently selected one
      return this.channels.filter(c => c.value !== this.currentChannel);
    }
  },
  watch: {
    modelValue: {
      handler(newVal) {
        if (newVal && newVal !== this.currentChannel) {
          this.currentChannel = newVal;
        }
      },
      immediate: true
    }
  },
  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    selectChannel(channel) {
      this.currentChannel = channel;
      this.isDropdownOpen = false;
      this.$emit('update:modelValue', channel);
      this.$emit('change', channel);
    }
  }
};
</script>

<style scoped>
.channel-selector {
  position: relative;
  display: inline-block;
}

/* Chip styling - matches Figma design */
.channel-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 20px;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #374957;
  white-space: nowrap;
  position: relative;
  z-index: 2;
  transition: background-color 0.2s ease;
}

.channel-chip.channel-in_app {
  background-color: #D6E7FA;
}

.channel-chip.channel-in_app:hover {
  background-color: #C5D7EB;
}

.channel-chip.channel-sms {
  background-color: #F8FFFB;
}

.channel-chip.channel-sms:hover {
  background-color: #E5F9EF;
}

.channel-chip.channel-email {
  background-color: #F0E6FC;
}

.channel-chip.channel-email:hover {
  background-color: #E1D3F1;
}

.channel-label {
  white-space: nowrap;
}

/* Chevron icon */
.dropdown-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M6 12L10 8L6 4' stroke='%23374957' stroke-width='2'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  transform: rotate(-90deg);
}

.dropdown-icon.is-open {
  transform: rotate(90deg);
}

/* Dropdown styling - appears above chip */
.channel-dropdown {
  position: absolute;
  bottom: 100%;
  left: 0;
  margin-bottom: -14px;
  padding: 4px 8px 16px 8px;
  background: white;
  border: 1px solid #C4C4C4;
  border-radius: 10px 10px 0 0;
  border-bottom: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 1;
  min-width: 83px;
}

.channel-option {
  display: block;
  width: 100%;
  border: none;
  background: none;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  letter-spacing: 0;
  color: #666666;
  text-align: left;
  padding: 4px 8px;
  white-space: nowrap;
  border-radius: 4px;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.channel-option:hover {
  color: #374957;
  background-color: #F5F5F5;
}

.option-label {
  white-space: nowrap;
}
</style>